import { create } from 'zustand';
import { AdapterType, type ConnectionProfile } from '@k-ucap/shared';
import { createRepositories } from '@/infrastructure/repositories/RepositoryFactory.js';

export interface ConnectionProfileState {
  profiles: ConnectionProfile[];
  selectedProfileId: string | null;
  isLoading: boolean;
  error: string | null;

  loadProfiles: (projectId: string) => Promise<void>;
  selectProfile: (id: string | null) => void;
  createProfile: (projectId: string, userId: string, environment: string) => Promise<ConnectionProfile>;
  updateProfile: (profile: ConnectionProfile) => Promise<void>;
  duplicateProfile: (id: string) => Promise<void>;
  deleteProfile: (id: string) => Promise<void>;
  clearError: () => void;
}

const createDefaultProfile = (projectId: string, userId: string, environment: string): ConnectionProfile => ({
  id: `conn-${Date.now()}`,
  projectId,
  name: '새 연동',
  description: '',
  adapterType: AdapterType.WEBSOCKET,
  enabled: true,
  direction: 'BIDIRECTIONAL',
  configuration: {
    url: 'ws://localhost:8080',
    token: '',
  },
  autoConnect: false,
  reconnectPolicy: {
    enabled: true,
    intervalMs: 3000,
    maxRetries: 10,
  },
  environment,
  createdBy: userId,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export const useConnectionProfileStore = create<ConnectionProfileState>((set, get) => {
  const { connectionProfileRepository } = createRepositories();

  return {
    profiles: [],
    selectedProfileId: null,
    isLoading: false,
    error: null,

    loadProfiles: async (projectId) => {
      set({ isLoading: true, error: null });
      try {
        const profiles = await connectionProfileRepository.findByProjectId(projectId);
        set({ profiles, isLoading: false });
      } catch (err) {
        set({ error: (err as Error).message, isLoading: false });
      }
    },

    selectProfile: (id) => set({ selectedProfileId: id }),

    createProfile: async (projectId, userId, environment) => {
      const newProfile = createDefaultProfile(projectId, userId, environment);
      await connectionProfileRepository.save(newProfile);
      set({ profiles: [...get().profiles, newProfile], selectedProfileId: newProfile.id });
      return newProfile;
    },

    updateProfile: async (profile) => {
      const updated: ConnectionProfile = { ...profile, updatedAt: new Date().toISOString() };
      await connectionProfileRepository.save(updated);
      set({
        profiles: get().profiles.map((p) => (p.id === updated.id ? updated : p)),
      });
    },

    duplicateProfile: async (id) => {
      const duplicated = await connectionProfileRepository.duplicate(id);
      if (!duplicated) return;
      set({ profiles: [...get().profiles, duplicated], selectedProfileId: duplicated.id });
    },

    deleteProfile: async (id) => {
      await connectionProfileRepository.delete(id);
      set({
        profiles: get().profiles.filter((p) => p.id !== id),
        selectedProfileId: get().selectedProfileId === id ? null : get().selectedProfileId,
      });
    },

    clearError: () => set({ error: null }),
  };
});

export default useConnectionProfileStore;
